import { createContext, useContext, useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatchTasks } from "../../../hooks/useDispatchTasks";
import { useNotification } from "../../../providers/NotificationProvider";
import { useEntities } from "@emberly/zenith-client";
import moment from "moment/moment";
import { getLocalStorageValue, sortByETA, sortByName } from "../../../common/helpers";
import OperationsMap from "./map/OperationsMap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MAP_LAYERS_OPEN, MAP_TASKS_OPEN } from "../../../store/actions";
import { DRIVERS_QUERY, FLEET_MGMT_INTEGRATION_QUERY } from "../../../common/queries";

export const OperationsMapContext = createContext();

export const useOperationsMap = () => useContext(OperationsMapContext);

export const OperationsMapProvider = (props) => {
  const settingsRef = useRef(OperationsMap.ReadSettings("main"));
  const settings = settingsRef.current;
  const { t } = useTranslation();


  const dispatch = useDispatch();
  const { mapLayersOpen: topLeftExpanded, mapTasksOpen: topRightExpanded } = useSelector(t => t.app);

  const setTopLeftExpanded = useCallback((val) => dispatch({ type: MAP_LAYERS_OPEN, payload: val }), [dispatch]);
  const setTopRightExpanded = useCallback((val) => dispatch({ type: MAP_TASKS_OPEN, payload: val }), [dispatch]);


  const [styleSelectorExpanded, setStyleSelectorExpanded] = useState(false);
  const [style, setStyle] = useState(settings.styleId || "standard");
  const [operationsMap, setOperationsMap] = useState(null);
  const [dispatchLayerHoverId, setDispatchLayerHoverId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const [missionTypeFilter, setMissionTypeFilter] = useState(settings?.plugins?.dispatch?.missionTypeFilter || "");
  const [taskStateFilter, setTaskStateFilter] = useState(settings?.plugins?.dispatch?.taskStateFilter || []);
  const [driverFilter, setDriverFilter] = useState(settings?.plugins?.dispatch?.driverFilter || "");
  const [orderTasksBy, setOrderTasksBy] = useState(getLocalStorageValue("operationmap_order_tasks_by", "new"));
  const [taskId, setTaskId] = useState(null);
  const { snackbar } = useNotification();

  const containerRef = useRef(null);

  const resetSettings = useCallback(() => {
    operationsMap?.resetSettings();
    setMissionTypeFilter("");
    setTaskStateFilter([]);
    setDriverFilter("");
    snackbar(t("operationsMap:resetSettingsSnackbar"));
  }, [operationsMap, snackbar, t]);

  const { activeTasks, completedTasks, initialLoad } = useDispatchTasks();
  const { entities: drivers } = useEntities("Employee", DRIVERS_QUERY);
  const { entities: vehicles } = useEntities("ServiceVehicle");
  const { entities: fleetIntegrations } = useEntities("Integration", FLEET_MGMT_INTEGRATION_QUERY);

  const filteredTasks = useMemo(() => {
    return activeTasks.concat(completedTasks)
      .filter(
        t =>
          (missionTypeFilter === "" || missionTypeFilter === t.missionDetails?.type) &&
          (taskStateFilter.length === 0 || !!taskStateFilter.find(f => f === t.state)) &&
          (driverFilter === "" || driverFilter === t.execution?.driver?.id)
      )
      .sort(
        (a, b) =>
          orderTasksBy === "new" ? moment(b.execution.history?.created?.created).diff(a.execution.history?.created?.created) :
            orderTasksBy === "old" ? moment(a.execution.history?.created?.created).diff(b.execution.history?.created?.created) :
              orderTasksBy === "state_asc" ? a.state - b.state :
                orderTasksBy === "state_desc" ? b.state - a.state :
                  orderTasksBy === "driver" ? sortByName(a, b) :
                    orderTasksBy === "eta" ? sortByETA(a, b) : 0
      )
  }, [activeTasks, missionTypeFilter, taskStateFilter, driverFilter, completedTasks, orderTasksBy]);

  useEffect(() => {
    operationsMap?.updateDispatchData(filteredTasks, completedTasks);
  }, [filteredTasks, completedTasks, operationsMap]);

  useEffect(() => {
    operationsMap?.updateVehicleData(vehicles);
  }, [vehicles, operationsMap]);

  useEffect(() => {
    operationsMap?.updateFleetIntegrations(fleetIntegrations);
  }, [fleetIntegrations, operationsMap]);

  const openTaskDialog = useCallback((taskId) => {
    setTaskId(taskId);
  }, []);

  const closeTaskDialog = useCallback(() => {
    setTaskId(null);
  }, []);

  return (
    <OperationsMapContext.Provider
      value={{
        topLeftExpanded,
        setTopLeftExpanded,
        topRightExpanded,
        setTopRightExpanded,
        styleSelectorExpanded,
        setStyleSelectorExpanded,
        dispatchLayerHoverId,
        setDispatchLayerHoverId,
        style,
        setStyle,
        drivers,
        containerRef,
        filteredTasks,
        activeTasks,
        completedTasks,
        operationsMap,
        setOperationsMap,
        initialLoad,
        missionTypeFilter,
        setMissionTypeFilter,
        taskStateFilter,
        setTaskStateFilter,
        driverFilter,
        setDriverFilter,
        openTaskDialog,
        closeTaskDialog,
        taskId,
        orderTasksBy,
        setOrderTasksBy,
        settings,
        resetSettings,
        loaded,
        setLoaded,
        fleetIntegrations
      }}
    >
      {props.children}
    </OperationsMapContext.Provider>
  );
};

